/**
 * Xano API client for the Bloxx customer dashboard. Talks to the Dashboard
 * API group in the Sidequester workspace (canonical `7cCIcwTp`).
 *
 * Auth strategy v1: JWT in localStorage, attached as `Authorization: Bearer`.
 */

const BASE = import.meta.env.VITE_XANO_BASE_URL;
const GROUP = import.meta.env.VITE_XANO_GROUP_DASHBOARD;

const TOKEN_KEY = "bloxx_dashboard_token";

export function getAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(TOKEN_KEY);
}

export function setAuthToken(token: string | null) {
  if (typeof window === "undefined") return;
  if (token) window.localStorage.setItem(TOKEN_KEY, token);
  else window.localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `Xano request failed with status ${status}`);
    this.status = status;
    this.body = body;
  }
}

interface RequestOpts extends Omit<RequestInit, "body"> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
}

async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  if (!BASE) throw new Error("VITE_XANO_BASE_URL is not configured");
  if (!GROUP) throw new Error("VITE_XANO_GROUP_DASHBOARD is not configured");

  const url = new URL(`${BASE}/api:${GROUP}${path}`);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
  }

  const headers = new Headers(opts.headers);
  headers.set("Accept", "application/json");
  if (opts.body !== undefined) headers.set("Content-Type", "application/json");

  const token = getAuthToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(url.toString(), {
    ...opts,
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
  });

  let payload: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!res.ok) {
    const message =
      typeof payload === "object" && payload && "message" in payload
        ? String((payload as { message: unknown }).message)
        : undefined;
    throw new ApiError(res.status, payload, message);
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, opts: Omit<RequestOpts, "method" | "body"> = {}) =>
    request<T>(path, { ...opts, method: "GET" }),
  post: <T>(path: string, body?: unknown, opts: Omit<RequestOpts, "method"> = {}) =>
    request<T>(path, { ...opts, method: "POST", body }),
  patch: <T>(path: string, body?: unknown, opts: Omit<RequestOpts, "method"> = {}) =>
    request<T>(path, { ...opts, method: "PATCH", body }),
  delete: <T>(path: string, opts: Omit<RequestOpts, "method" | "body"> = {}) =>
    request<T>(path, { ...opts, method: "DELETE" }),
};
